import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { usuariosApi, Usuario, AuthUser } from '../api/api';

const TELAS_DISPONIVEIS = [
  { path: '/', label: 'Sessões' },
  { path: '/disparador', label: 'Disparador' },
  { path: '/bases', label: 'Importar Bases' },
  { path: '/categorias', label: 'Categorias' },
  { path: '/templates', label: 'Modelos de Mensagens' },
  { path: '/usuarios', label: 'Usuários' },
  { path: '/leads', label: 'Leads Finalizados' },
  { path: '/leads-pendentes', label: 'Leads Pendentes' },
];

interface Props {
  currentUser: AuthUser;
}

export default function UsuariosPage({ currentUser }: Props) {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [warn, setWarn] = useState<string | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  // Form fields
  const [nomeCompleto, setNomeCompleto] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [tipo, setTipo] = useState<'operador' | 'administrador'>('operador');
  const [telas, setTelas] = useState<string[]>([]);
  const [maxSessoes, setMaxSessoes] = useState(0);
  const [apenasPropriosDados, setApenasPropriosDados] = useState(false);
  const [receberLeads, setReceberLeads] = useState(false);
  const [foto, setFoto] = useState<string | null>(null);
  const fotoInputRef = useRef<HTMLInputElement>(null);

  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warnTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = (msg: string) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast(msg);
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  };

  const showWarn = (msg: string) => {
    if (warnTimer.current) clearTimeout(warnTimer.current);
    setWarn(msg);
    warnTimer.current = setTimeout(() => setWarn(null), 3000);
  };

  const loadUsuarios = useCallback(async () => {
    try {
      const { data } = await usuariosApi.list();
      setUsuarios(data);
    } catch {}
  }, []);

  useEffect(() => { loadUsuarios(); }, [loadUsuarios]);

  const openModal = (user?: Usuario) => {
    if (user) {
      setEditId(user.id);
      setNomeCompleto(user.nomeCompleto);
      setEmail(user.email);
      setSenha('');
      setTipo(user.tipo);
      setTelas(user.telas || []);
      setMaxSessoes(user.maxSessoes ?? 0);
      setApenasPropriosDados((user as any).apenasPropriosDados ?? false);
      setReceberLeads((user as any).receberLeads ?? false);
      setFoto(user.foto || null);
    } else {
      setEditId(null);
      setNomeCompleto('');
      setEmail('');
      setSenha('');
      setTipo('operador');
      setTelas([]);
      setMaxSessoes(0);
      setApenasPropriosDados(false);
      setReceberLeads(false);
      setFoto(null);
    }
    setModalOpen(true);
  };

  const handleFotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setFoto(reader.result as string);
    reader.readAsDataURL(file);
  };

  const toggleTela = (path: string) => {
    setTelas(prev =>
      prev.includes(path) ? prev.filter(t => t !== path) : [...prev, path]
    );
  };

  const handleSave = async () => {
    if (!nomeCompleto.trim()) { showWarn('Nome completo é obrigatório'); return; }
    if (!email.trim()) { showWarn('E-mail é obrigatório'); return; }
    if (editId === null && !senha.trim()) { showWarn('Senha é obrigatória'); return; }

    // Verificar email duplicado na lista local
    const emailNorm = email.trim().toLowerCase();
    const duplicate = usuarios.find(u => u.email.toLowerCase() === emailNorm && u.id !== editId);
    if (duplicate) { showWarn('Já existe um usuário com esse e-mail'); return; }

    setLoading(true);
    try {
      const payload = {
        nome_completo: nomeCompleto.trim(),
        email: emailNorm,
        tipo,
        telas: tipo === 'administrador' ? [] : telas,
        max_sessoes: maxSessoes,
        apenas_proprios_dados: apenasPropriosDados,
        receber_leads: receberLeads,
        foto: foto ?? null,
        ...(senha.trim() ? { senha: senha.trim() } : {}),
      };

      if (editId !== null) {
        await usuariosApi.update(editId, payload);
        showToast('Usuário atualizado com sucesso');
      } else {
        await usuariosApi.create({ ...payload, senha: senha.trim() });
        showToast('Usuário criado com sucesso');
      }
      setModalOpen(false);
      await loadUsuarios();
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Erro ao salvar usuário';
      showWarn(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAtivo = async (user: Usuario) => {
    try {
      await usuariosApi.toggleAtivo(user.id);
      await loadUsuarios();
      showToast(user.ativo ? 'Usuário inativado com sucesso' : 'Usuário ativado com sucesso');
    } catch {
      showWarn('Erro ao alterar status');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await usuariosApi.remove(id);
      await loadUsuarios();
      showToast('Usuário excluído com sucesso');
    } catch {
      showWarn('Erro ao excluir usuário');
    } finally {
      setDeleteConfirmId(null);
    }
  };

  const isAdmin = currentUser.tipo === 'administrador';

  const filtered = usuarios
    .filter(u => isAdmin || u.id === currentUser.id)
    .filter(u =>
      !search.trim() ||
      u.nomeCompleto.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase())
    );

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        {isAdmin && (
          <button
            type="button"
            onClick={() => openModal()}
            className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded-lg transition-all hover:bg-gray-800 flex-shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
            </svg>
            Novo Usuário
          </button>
        )}
        <div className="flex items-center gap-2 border rounded-lg px-3 py-2 w-64 bg-white border border-gray-200">
          <svg className="w-4 h-4 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Nome ou e-mail..."
            className="flex-1 bg-transparent text-sm text-gray-900 placeholder-gray-400 focus:outline-none"
          />
        </div>
      </div>

      {/* Tabela */}
      <div className="overflow-hidden rounded-lg border border-gray-200">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-4 py-2.5 w-10"></th>
              <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400">Nome Completo</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400">Email</th>
              <th className="text-center px-4 py-2.5 text-xs font-medium text-gray-400">Tipo</th>
              <th className="text-center px-4 py-2.5 text-xs font-medium text-gray-400">Sessões</th>
              <th className="text-center px-4 py-2.5 text-xs font-medium text-gray-400">Status</th>
              <th className="px-4 py-2.5 w-28 text-xs font-medium text-gray-400">Ações</th>
            </tr>
          </thead>
          <tbody className=" bg-white border border-gray-200">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-20 text-center text-gray-400 text-sm">
                  {usuarios.length === 0 ? 'Nenhum usuário cadastrado' : 'Nenhum usuário encontrado'}
                </td>
              </tr>
            ) : (
              filtered.map((user, i) => (
                <tr
                  key={user.id}
                  className={`hover:bg-gray-50 transition-colors ${i !== filtered.length - 1 ? 'border-b border-gray-100' : ''}`}
                >
                  <td className="px-4 py-3">
                    {user.foto ? (
                      <img src={user.foto} alt="" className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-xs font-semibold text-gray-400">
                        {user.nomeCompleto.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{user.nomeCompleto}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{user.email}</td>
                  <td className="px-4 py-3 text-center">
                    {user.tipo === 'administrador' ? (
                      <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[rgba(168,85,247,0.15)] text-[#C4B5FD] border border-[rgba(168,85,247,0.3)]">
                        Administrador
                      </span>
                    ) : (
                      <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-semibold border border-blue-200 bg-blue-50 text-blue-700">
                        Operador
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200">
                      {user.maxSessoes === 0 ? '∞' : user.maxSessoes}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${user.ativo ? ' border-emerald-200' : ' border-red-200'} bg-emerald-50 text-emerald-700`}>
                      {user.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => handleToggleAtivo(user)}
                        title={user.ativo ? 'Desativar' : 'Ativar'}
                        className={`relative inline-flex items-center w-8 h-4 rounded-full transition-colors duration-200 focus:outline-none ${user.ativo ? 'bg-emerald-500' : 'bg-red-400'}`}
                      >
                        <span className={`inline-block w-2.5 h-2.5 rounded-full shadow transition-transform duration-200 ${user.ativo ? 'translate-x-4' : 'translate-x-0.5'} bg-white border border-gray-200`} />
                      </button>
                      <button
                        type="button"
                        onClick={() => openModal(user)}
                        className="w-7 h-7 flex items-center justify-center rounded transition-all text-amber-500 hover:text-yellow-700 hover:bg-yellow-50"
                        title="Editar usuário"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteConfirmId(user.id)}
                        className="w-7 h-7 flex items-center justify-center rounded transition-all text-red-500 hover:text-red-500 hover:bg-red-50"
                        title="Excluir usuário"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Criar/Editar */}
      {modalOpen && createPortal(
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="border rounded-2xl p-8 max-w-2xl w-full shadow-xl bg-white border border-gray-200">
            <div className="text-center mb-6">
              <h3 className="text-base font-bold text-gray-900">{editId !== null ? 'Editar Usuário' : 'Novo Usuário'}</h3>
              <p className="text-sm text-gray-400 mt-1">Preencha os dados do usuário</p>
            </div>
            {/* Avatar picker */}
            <div className="flex justify-center mb-5">
              <button
                type="button"
                onClick={() => fotoInputRef.current?.click()}
                className="relative group"
                title="Clique para escolher foto"
              >
                {foto ? (
                  <img src={foto} alt="" className="w-20 h-20 rounded-full object-cover border-2 border-gray-200" />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-300">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                )}
                <div className="absolute inset-0 rounded-full bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
              </button>
              <input
                ref={fotoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFotoChange}
              />
            </div>

            <div className="mb-6 space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <input
                  type="text"
                  value={nomeCompleto}
                  onChange={e => setNomeCompleto(e.target.value)}
                  placeholder="Nome completo"
                  className="border rounded-xl px-3 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-400 transition-all bg-white border border-gray-200"
                />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="E-mail"
                  className="border rounded-xl px-3 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-400 transition-all bg-white border border-gray-200"
                />
                <input
                  type="password"
                  value={senha}
                  onChange={e => setSenha(e.target.value)}
                  placeholder={editId !== null ? 'Nova senha (opcional)' : 'Senha'}
                  className="border rounded-xl px-3 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-400 transition-all bg-white border border-gray-200"
                />
                <select
                  value={tipo}
                  onChange={e => setTipo(e.target.value as 'operador' | 'administrador')}
                  title="Tipo de usuário"
                  className="border rounded-xl px-3 py-3 text-sm text-gray-900 focus:outline-none focus:border-gray-400 transition-all appearance-none bg-white border border-gray-200"
                >
                  <option value="operador">Operador</option>
                  <option value="administrador">Administrador</option>
                </select>
                <div className="relative">
                  <input
                    type="number"
                    min={0}
                    max={9999}
                    value={maxSessoes}
                    onChange={e => setMaxSessoes(Math.min(9999, Math.max(0, parseInt(e.target.value) || 0)))}
                    placeholder="0"
                    className="w-full border rounded-xl px-3 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-400 transition-all bg-white border border-gray-200"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-300 pointer-events-none">sessões</span>
                </div>
              </div>

              {tipo === 'operador' && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
                  <p className="text-xs text-gray-400 font-medium mb-3">Telas que pode acessar</p>
                  <div className="grid grid-cols-3 gap-x-2 gap-y-2.5">
                    {TELAS_DISPONIVEIS.map(tela => (
                      <label key={tela.path} className="flex items-center gap-2 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={telas.includes(tela.path)}
                          onChange={() => toggleTela(tela.path)}
                          className="w-4 h-4 rounded accent-emerald-500 cursor-pointer flex-shrink-0"
                        />
                        <span className="text-xs text-gray-600 group-hover:text-gray-900 transition-colors leading-tight">{tela.label}</span>
                      </label>
                    ))}
                  </div>
                  <div className="border-t border-gray-200 mt-3 pt-3">
                    <label className="flex items-center gap-2 cursor-pointer group w-fit">
                      <input
                        type="checkbox"
                        checked={apenasPropriosDados}
                        onChange={e => setApenasPropriosDados(e.target.checked)}
                        className="w-4 h-4 rounded accent-emerald-500 cursor-pointer flex-shrink-0"
                      />
                      <span className="text-xs text-gray-600 group-hover:text-gray-900 transition-colors">Ver apenas dados dele</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer group w-fit mt-2">
                      <input
                        type="checkbox"
                        checked={receberLeads}
                        onChange={e => setReceberLeads(e.target.checked)}
                        className="w-4 h-4 rounded accent-emerald-500 cursor-pointer flex-shrink-0"
                      />
                      <span className="text-xs text-gray-600 group-hover:text-gray-900 transition-colors">Receber leads</span>
                    </label>
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="flex-1 py-3 bg-gray-100 hover:bg-gray-100 text-gray-600 rounded-xl text-sm font-medium transition-all border border-gray-200"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={loading}
                className="flex-1 py-3 bg-gray-900 hover:bg-gray-800 text-white rounded-xl text-sm font-semibold transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              >
                {loading ? 'Salvando...' : editId !== null ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal Confirmar Exclusão */}
      {deleteConfirmId !== null && createPortal(
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="border rounded-2xl p-8 max-w-sm w-full shadow-xl bg-white border border-gray-200">
            <div className="text-center mb-6">
              <div className="w-12 h-12 rounded-xl bg-red-50 border border-red-200 flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-base font-bold text-gray-900">Excluir Usuário</h3>
              <p className="text-sm text-gray-600 mt-1">
                Tem certeza que deseja excluir <span className="text-gray-900 font-medium">"{usuarios.find(u => u.id === deleteConfirmId)?.nomeCompleto}"</span>? Esta ação não pode ser desfeita.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirmId(null)}
                className="flex-1 py-3 bg-gray-100 hover:bg-gray-100 text-gray-600 rounded-xl text-sm font-medium transition-all border border-gray-200"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => handleDelete(deleteConfirmId)}
                className="flex-1 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-semibold transition-all"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Toast sucesso */}
      {toast && createPortal(
        <div className="fixed top-0 right-0 z-[99999] flex items-center gap-3 bg-emerald-500 text-white px-5 py-4 shadow-2xl rounded-bl-xl toast-slide-in">
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
          <span className="text-sm font-semibold">{toast}</span>
        </div>,
        document.body
      )}

      {/* Toast alerta */}
      {warn && createPortal(
        <div className="fixed top-0 right-0 z-[99999] flex items-center gap-3 bg-amber-500 text-white px-5 py-4 shadow-2xl rounded-bl-xl toast-slide-in">
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <span className="text-sm font-semibold">{warn}</span>
        </div>,
        document.body
      )}
    </div>
  );
}
